import React, { useEffect, useRef, useState, useCallback } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import rehypeRaw from 'rehype-raw';
import { Modal } from '../Modal';
import { Box, Button, Typography } from '@mui/material';
import { Download as DownloadIcon } from '@mui/icons-material';
import { useHelp } from '../../../application/context/HelpContext';
import { urlMatchesPattern } from '../../../pages/Help/utils/urlMatcher';

interface HelpIconInjectorProps {
  pageUrl?: string;
}

/** Shared `a` renderer: download button for /statics/ URLs, regular link otherwise */
const ARenderer = ({ href, children, ...props }: React.AnchorHTMLAttributes<HTMLAnchorElement> & { children?: React.ReactNode }) => {
  const isAttachment = href && (href.includes('/statics/') || href.includes('blob.core.windows.net'));
  if (isAttachment) {
    return (
      <Button
        component="a"
        href={href}
        target="_blank"
        rel="noopener noreferrer"
        download
        size="small"
        variant="outlined"
        startIcon={<DownloadIcon fontSize="small" />}
        sx={{
          my: 0.5,
          textTransform: 'none',
          fontSize: '0.875rem',
          borderColor: '#00B6BA',
          color: '#00B6BA',
          '&:hover': { borderColor: '#008a8d', color: '#008a8d', backgroundColor: 'rgba(0,182,186,0.04)' },
        }}
        {...(props as any)}
      >
        {children}
      </Button>
    );
  }
  return (
    <a href={href} target="_blank" rel="noopener noreferrer" {...props}>
      {children}
    </a>
  );
};

/** Extract attachment URL+filename from a Global entry, using attachmentList first,
 *  then falling back to parsing the markdown link in content. */
function extractGlobalAttachment(item: any): { url: string; filename: string } | null {
  if (item.attachmentList?.length > 0) {
    return { url: item.attachmentList[0].url, filename: item.attachmentList[0].filename || '' };
  }
  // Fallback: parse [filename](url) from content
  const match = (item.content as string | undefined)?.trim().match(/^\[([^\]]*)\]\(([^)]+)\)/);
  if (match) {
    return { filename: match[1], url: match[2] };
  }
  return null;
}

const HelpIconInjector: React.FC<HelpIconInjectorProps> = ({ pageUrl }) => {
  const injectedRefs = useRef<Set<string>>(new Set());
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalContent, setModalContent] = useState<any[]>([]);
  const [modalDataTestId, setModalDataTestId] = useState<string>('');

  // Subscribe to React context so we re-run when help content loads
  const { helpContent: contextHelpContent } = useHelp();

  // Keep a stable ref to the latest inject function so observers don't need to
  // be torn down and re-created every time helpContent changes.
  const injectHelpIconsRef = useRef<() => void>(() => {});

  // Function to show help modal
  const showHelpModal = (helpContent: any[], dataTestId: string) => {
    setModalContent(helpContent);
    setModalDataTestId(dataTestId);
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setModalContent([]);
    setModalDataTestId('');
  };

  // Build the inject function; recreated whenever contextHelpContent or pageUrl changes
  const injectHelpIcons = useCallback(() => {
    // Clear existing injected icons first
    injectedRefs.current.forEach(elementId => {
      const helpIcon = document.querySelector(`[data-help-id="${elementId}"]`);
      if (helpIcon) {
        helpIcon.remove();
      }
    });
    injectedRefs.current.clear();

    // Derive content to display from the React context (always up-to-date)
    const helpContent = pageUrl
      ? contextHelpContent.filter(c => c.enabled && urlMatchesPattern(pageUrl, c.pageurl))
      : contextHelpContent.filter(c => c.enabled);

    if (helpContent.length === 0) return;

    // Group help content by dataTestId
    const helpByDataTestId = helpContent.reduce((acc: Record<string, any[]>, content: any) => {
      if (content.enabled && content.datatestid) {
        if (!acc[content.datatestid]) {
          acc[content.datatestid] = [];
        }
        acc[content.datatestid].push(content);
      }
      return acc;
    }, {});

    // For each dataTestId, find elements and inject help icons
    Object.keys(helpByDataTestId).forEach(dataTestId => {
      const items = helpByDataTestId[dataTestId];

      // Global attachment entries: add a direct click listener on the element itself
      // instead of injecting a ? button, to avoid conflicts with native click handlers.
      if (items.length === 1 && items[0].moduleid === 'Global') {
        const attachment = extractGlobalAttachment(items[0]);
        if (attachment) {
          const elements = document.querySelectorAll(`[data-testid="${dataTestId}"]`);
          elements.forEach((element) => {
            // Avoid adding duplicate listeners by flagging the element
            if ((element as HTMLElement).dataset.helpGlobalBound === '1') return;
            (element as HTMLElement).dataset.helpGlobalBound = '1';
            element.addEventListener('click', () => {
              const a = document.createElement('a');
              a.href = attachment.url;
              a.target = '_blank';
              a.rel = 'noopener noreferrer';
              a.download = attachment.filename;
              document.body.appendChild(a);
              a.click();
              document.body.removeChild(a);
            });
          });
        }
        return; // skip ? button injection for Global entries
      }

      const elements = document.querySelectorAll(`[data-testid="${dataTestId}"]`);

      elements.forEach((element, index) => {
        const elementId = `${dataTestId}-${index}`;

        // Skip if already injected
        if (injectedRefs.current.has(elementId)) {
          return;
        }

        // Check if help icon is already present
        if (element.querySelector('.help-icon-injected')) {
          return;
        }

        // Create container for help icon
        const helpContainer = document.createElement('div');
        helpContainer.className = 'help-icon-injected';
        helpContainer.setAttribute('data-help-id', elementId);
        helpContainer.style.cssText = `
          position: absolute;
          top: 8px;
          right: 8px;
          z-index: 1000;
          pointer-events: auto;
          background: white;
          border-radius: 50%;
          box-shadow: 0 2px 4px rgba(0,0,0,0.2);
          width: 32px;
          height: 32px;
          display: flex;
          align-items: center;
          justify-content: center;
        `;

        // Make sure parent element has relative positioning
        const computedStyle = window.getComputedStyle(element);
        if (computedStyle.position === 'static') {
          (element as HTMLElement).style.position = 'relative';
        }

        // Ensure parent element has minimum height for the icon
        const elementHeight = element.getBoundingClientRect().height;
        if (elementHeight < 40) {
          (element as HTMLElement).style.minHeight = '40px';
        }

        // Append container to element
        element.appendChild(helpContainer);

        // Create a simple help icon with modal functionality
        const helpIcon = document.createElement('div');
        helpIcon.style.cssText = `
          width: 24px;
          height: 24px;
          background: #1976d2;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          color: white;
          font-weight: bold;
          font-size: 14px;
          font-family: Arial, Helvetica, sans-serif;
          line-height: 1;
          text-align: center;
          box-sizing: border-box;
          cursor: pointer;
          transition: background-color 0.2s;
        `;
        helpIcon.textContent = '?';
        helpIcon.title = 'Help';

        // Add hover effect
        helpIcon.addEventListener('mouseenter', () => {
          helpIcon.style.backgroundColor = '#1565c0';
        });
        helpIcon.addEventListener('mouseleave', () => {
          helpIcon.style.backgroundColor = '#1976d2';
        });

        // Add click handler for modal
        helpIcon.addEventListener('click', (e) => {
          e.stopPropagation();
          showHelpModal(items, dataTestId);
        });

        helpContainer.appendChild(helpIcon);

        // Mark as injected
        injectedRefs.current.add(elementId);
      });
    });
  }, [contextHelpContent, pageUrl]);

  // Keep the ref pointing to the latest version of injectHelpIcons
  useEffect(() => {
    injectHelpIconsRef.current = injectHelpIcons;
  }, [injectHelpIcons]);

  // Re-inject whenever help content changes (the core fix: context → inject)
  useEffect(() => {
    const timeoutId = setTimeout(injectHelpIcons, 100);
    return () => clearTimeout(timeoutId);
  }, [injectHelpIcons]);

  // Set up observers and route listeners once; they call the ref so they always
  // use the latest inject function without needing to be recreated.
  useEffect(() => {
    // Set up MutationObserver to watch for DOM changes
    const observer = new MutationObserver((mutations) => {
      let shouldReinject = false;
      
      mutations.forEach((mutation) => {
        if (mutation.type === 'childList' && mutation.addedNodes.length > 0) {
          mutation.addedNodes.forEach((node) => {
            if (node.nodeType === Node.ELEMENT_NODE) {
              const element = node as Element;
              if (element.hasAttribute('data-testid') || 
                  element.querySelector('[data-testid]')) {
                shouldReinject = true;
              }
            }
          });
        }
      });

      if (shouldReinject) {
        setTimeout(() => injectHelpIconsRef.current(), 100);
      }
    });

    // Start observing
    observer.observe(document.body, {
      childList: true,
      subtree: true
    });

    // Listen for route changes (popstate event for browser back/forward)
    const handleRouteChange = () => {
      setTimeout(() => injectHelpIconsRef.current(), 200);
    };

    window.addEventListener('popstate', handleRouteChange);

    // Also listen for pushstate/replacestate (for programmatic navigation)
    const originalPushState = history.pushState;
    const originalReplaceState = history.replaceState;

    history.pushState = function(...args) {
      originalPushState.apply(history, args);
      setTimeout(handleRouteChange, 100);
    };

    history.replaceState = function(...args) {
      originalReplaceState.apply(history, args);
      setTimeout(handleRouteChange, 100);
    };

    // Cleanup
    return () => {
      observer.disconnect();
      window.removeEventListener('popstate', handleRouteChange);
      history.pushState = originalPushState;
      history.replaceState = originalReplaceState;
      
      // Clean up injected help icons
      injectedRefs.current.forEach(elementId => {
        const helpIcon = document.querySelector(`[data-help-id="${elementId}"]`);
        if (helpIcon) {
          helpIcon.remove();
        }
      });
      injectedRefs.current.clear();
    };
  }, []); // observers set up only once

  // Render the modal content
  const renderModalContent = () => {
    if (modalContent.length === 1) {
      const helpItem = modalContent[0];
      
      return (
        <Box>
          <Typography variant="h6" sx={{ mb: 2, color: '#333', fontWeight: 600 }}>
            {helpItem.title}
          </Typography>
          <Box sx={{ 
            lineHeight: 1.6, 
            color: '#555',
            '& h1, & h2, & h3, & h4, & h5, & h6': {
              marginTop: '1rem',
              marginBottom: '0.5rem',
              fontWeight: 600,
              color: '#063142',
            },
            '& h1': {
              fontSize: '1.25rem',
              borderBottom: '2px solid #E1E8ED',
              paddingBottom: '0.25rem',
            },
            '& h2': {
              fontSize: '1.1rem',
              borderBottom: '1px solid #E1E8ED',
              paddingBottom: '0.125rem',
            },
            '& h3': {
              fontSize: '1rem',
            },
            '& p': {
              marginBottom: '0.5rem',
              lineHeight: 1.4,
            },
            '& ul, & ol': {
              marginBottom: '0.5rem',
              paddingLeft: '1.5rem',
            },
            '& li': {
              marginBottom: '0.125rem',
            },
            '& code': {
              backgroundColor: '#f5f5f5',
              padding: '0.125rem 0.25rem',
              borderRadius: '0.25rem',
              fontSize: '0.875rem',
              fontFamily: 'monospace',
            },
            '& pre': {
              backgroundColor: '#f8f9fa',
              padding: '0.5rem',
              borderRadius: '0.25rem',
              overflowX: 'auto',
              border: '1px solid #e9ecef',
              marginBottom: '0.5rem',
            },
            '& pre code': {
              backgroundColor: 'transparent',
              padding: 0,
            },
            '& blockquote': {
              borderLeft: '3px solid #00B6BA',
              paddingLeft: '0.75rem',
              marginLeft: 0,
              fontStyle: 'italic',
              color: '#666',
            },
            '& table': {
              borderCollapse: 'collapse',
              width: '100%',
              marginBottom: '0.5rem',
            },
            '& th, & td': {
              border: '1px solid #ddd',
              padding: '0.25rem',
              textAlign: 'left',
              fontSize: '0.875rem',
            },
            '& th': {
              backgroundColor: '#f8f9fa',
              fontWeight: 600,
            },
            '& a': {
              color: '#00B6BA',
              textDecoration: 'none',
            },
            '& a:hover': {
              textDecoration: 'underline',
            },
            '& strong': {
              fontWeight: 600,
            },
            '& em': {
              fontStyle: 'italic',
            },
            '& img': {
              maxWidth: '100%',
              height: 'auto',
              borderRadius: '4px',
              boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
            },
            '& video': {
              maxWidth: '100%',
              height: 'auto',
              borderRadius: '4px',
              boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
            }
          }}>
            <ReactMarkdown
              remarkPlugins={[remarkGfm]}
              rehypePlugins={[rehypeRaw]}
              components={{
                a: ARenderer,
                img: ({ src, alt, ...props }) => {
                  // Log image URL with token info
                  console.log('🖼️ Image URL:', src);
                  
                  return (
                    <img 
                      src={src} 
                      alt={alt || 'Image'} 
                      style={{
                        maxWidth: '100%',
                        height: 'auto',
                        borderRadius: '4px',
                        boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
                        display: 'block',
                        margin: '10px 0',
                      }}
                      onLoad={() => {
                        console.log('✅ Loaded:', alt || 'Image');
                      }}
                      onError={() => {
                        console.log('❌ Failed:', src);
                      }}
                      {...props}
                    />
                  );
                },
              }}
            >
              {helpItem.content || 'No content available'}
            </ReactMarkdown>
          </Box>
        </Box>
      );
    } else {
      return (
        <Box>
          {modalContent.map((helpItem, index) => (
            <Box key={index} sx={{ mb: 2, border: '1px solid #e0e0e0', borderRadius: 1, overflow: 'hidden' }}>
              <Box sx={{ 
                background: '#f5f5f5', 
                padding: '12px 16px', 
                cursor: 'pointer', 
                fontWeight: 500, 
                color: '#333',
                borderBottom: '1px solid #e0e0e0',
                '&:hover': {
                  backgroundColor: '#e3f2fd',
                }
              }}>
                {index + 1}. {helpItem.title}
              </Box>
              <Box sx={{ 
                padding: 2, 
                lineHeight: 1.6, 
                color: '#555',
                '& h1, & h2, & h3, & h4, & h5, & h6': {
                  marginTop: '1rem',
                  marginBottom: '0.5rem',
                  fontWeight: 600,
                  color: '#063142',
                },
                '& h1': {
                  fontSize: '1.25rem',
                  borderBottom: '2px solid #E1E8ED',
                  paddingBottom: '0.25rem',
                },
                '& h2': {
                  fontSize: '1.1rem',
                  borderBottom: '1px solid #E1E8ED',
                  paddingBottom: '0.125rem',
                },
                '& h3': {
                  fontSize: '1rem',
                },
                '& p': {
                  marginBottom: '0.5rem',
                  lineHeight: 1.4,
                },
                '& ul, & ol': {
                  marginBottom: '0.5rem',
                  paddingLeft: '1.5rem',
                },
                '& li': {
                  marginBottom: '0.125rem',
                },
                '& code': {
                  backgroundColor: '#f5f5f5',
                  padding: '0.125rem 0.25rem',
                  borderRadius: '0.25rem',
                  fontSize: '0.875rem',
                  fontFamily: 'monospace',
                },
                '& pre': {
                  backgroundColor: '#f8f9fa',
                  padding: '0.5rem',
                  borderRadius: '0.25rem',
                  overflowX: 'auto',
                  border: '1px solid #e9ecef',
                  marginBottom: '0.5rem',
                },
                '& pre code': {
                  backgroundColor: 'transparent',
                  padding: 0,
                },
                '& blockquote': {
                  borderLeft: '3px solid #00B6BA',
                  paddingLeft: '0.75rem',
                  marginLeft: 0,
                  fontStyle: 'italic',
                  color: '#666',
                },
                '& table': {
                  borderCollapse: 'collapse',
                  width: '100%',
                  marginBottom: '0.5rem',
                },
                '& th, & td': {
                  border: '1px solid #ddd',
                  padding: '0.25rem',
                  textAlign: 'left',
                  fontSize: '0.875rem',
                },
                '& th': {
                  backgroundColor: '#f8f9fa',
                  fontWeight: 600,
                },
                '& a': {
                  color: '#00B6BA',
                  textDecoration: 'none',
                },
                '& a:hover': {
                  textDecoration: 'underline',
                },
                '& strong': {
                  fontWeight: 600,
                },
                '& em': {
                  fontStyle: 'italic',
                },
                '& img': {
                  maxWidth: '100%',
                  height: 'auto',
                  borderRadius: '4px',
                  boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
                },
                '& video': {
                  maxWidth: '100%',
                  height: 'auto',
                  borderRadius: '4px',
                  boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
                }
              }}>
                <ReactMarkdown
                  remarkPlugins={[remarkGfm]}
                  rehypePlugins={[rehypeRaw]}
                  components={{
                    a: ARenderer,
                    img: ({ src, alt, ...props }) => {
                      // Log image URL with token info
                      console.log('🖼️ Image URL:', src);
                      
                      return (
                        <img 
                          src={src} 
                          alt={alt || 'Image'} 
                          style={{
                            maxWidth: '100%',
                            height: 'auto',
                            borderRadius: '4px',
                            boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
                            display: 'block',
                            margin: '10px 0',
                          }}
                          onLoad={() => {
                            console.log('✅ Loaded:', alt || 'Image');
                          }}
                          onError={() => {
                            console.log('❌ Failed:', src);
                          }}
                          {...props}
                        />
                      );
                    },
                  }}
                >
                  {helpItem.content || 'No content available'}
                </ReactMarkdown>
              </Box>
            </Box>
          ))}
        </Box>
      );
    }
  };

  return (
    <>
      {/* Help Modal */}
      <Modal
        isOpen={isModalOpen}
        onClose={closeModal}
        title={`Help Content - ${modalDataTestId}`}
        maxWidth="md"
        fullWidth
        scroll="paper"
        disableScrollLock={true}
        disableEscapeKeyDown={false}
        disableSave={true}
        PaperProps={{
          sx: {
            maxHeight: '90vh',
            height: 'auto',
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
            margin: 'auto',
            '& .MuiDialogContent-root': {
              overflow: 'auto',
              flex: 1,
            }
          }
        }}
      >
        {renderModalContent()}
      </Modal>
    </>
  );
};

export default HelpIconInjector;
